const COLORS = {
    burgundy: "#6B2737",
    burgundyDeep: "#541D2A",
    cream: "#FDF9F4",
    gold: "#D4AF7A",
    goldDeep: "#C69F65",
    rose: "#E8B4B8",
};

const EVOCATIVE = {
    "devoted-idealist": "Love that finally sees you clearly.",
    "quiet-romantic": "The wall was never the whole story.",
    "curious-explorer": "Chemistry you don't have to chase.",
    "steady-anchor": "The calm that makes love last.",
    "bold-catalyst": "Fire that meets its match.",
    "thoughtful-strategist": "Depth deserves depth.",
    "magnetic-visionary": "You light rooms. Find who lights you.",
    "grounded-realist": "Real love, eyes open.",
};

function wrapText(ctx, text, maxWidth) {
    const words = text.split(" ");
    const lines = [];
    let line = "";
    for (const w of words) {
        const test = line ? `${line} ${w}` : w;
        if (ctx.measureText(test).width > maxWidth && line) {
            lines.push(line);
            line = w;
        } else {
            line = test;
        }
    }
    if (line) lines.push(line);
    return lines;
}

async function ensureFonts() {
    try {
        await Promise.all([
            document.fonts.load("900 100px 'Inter'"),
            document.fonts.load("600 40px 'Inter'"),
            document.fonts.load("400 40px 'Inter'"),
        ]);
    } catch { /* system fallback */ }
}

/** Render the branded archetype card. format: "feed" (1080x1350) | "story" (1080x1920). Returns a Blob. */
export async function renderShareCard({ archetype, referralCode, format = "story" }) {
    await ensureFonts();
    const W = 1080;
    const H = format === "feed" ? 1350 : 1920;
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");

    // Background
    ctx.fillStyle = COLORS.burgundyDeep;
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = COLORS.burgundy;
    ctx.beginPath();
    ctx.ellipse(W * 0.85, H * 0.12, W * 0.7, H * 0.35, 0.4, 0, Math.PI * 2);
    ctx.fill();

    // Gold frame
    const m = 54;
    ctx.strokeStyle = COLORS.gold;
    ctx.lineWidth = 3;
    ctx.strokeRect(m, m, W - m * 2, H - m * 2);
    ctx.strokeStyle = "rgba(212,175,122,0.35)";
    ctx.lineWidth = 1;
    ctx.strokeRect(m + 14, m + 14, W - (m + 14) * 2, H - (m + 14) * 2);

    const cx = W / 2;
    const story = format === "story";
    const top = story ? 420 : 200;
    const g = story ? 1.35 : 1;

    // Eyebrow
    ctx.textAlign = "center";
    ctx.fillStyle = COLORS.gold;
    ctx.font = "600 34px 'Inter', sans-serif";
    ctx.fillText("M Y   L O V E   A R C H E T Y P E", cx, top);

    // Archetype name (shrink when it wraps past 2 lines)
    ctx.fillStyle = COLORS.cream;
    let nameSize = 118;
    ctx.font = `900 ${nameSize}px 'Inter', sans-serif`;
    let nameLines = wrapText(ctx, archetype.name, W - 220);
    if (nameLines.length > 2) {
        nameSize = 92;
        ctx.font = `900 ${nameSize}px 'Inter', sans-serif`;
        nameLines = wrapText(ctx, archetype.name, W - 200);
    }
    let y = top + 170 * g;
    nameLines.forEach(line => {
        ctx.fillText(line, cx, y);
        y += nameSize + 10;
    });

    // Divider
    y += 8 * g;
    ctx.strokeStyle = COLORS.gold;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(cx - 90, y);
    ctx.lineTo(cx + 90, y);
    ctx.stroke();
    y += 90 * g;

    // Tagline
    ctx.fillStyle = "rgba(253,249,244,0.92)";
    ctx.font = "400 44px 'Inter', sans-serif";
    wrapText(ctx, archetype.tagline, W - 260).forEach(line => {
        ctx.fillText(line, cx, y);
        y += 62;
    });
    y += 46 * g;

    // Evocative line
    ctx.fillStyle = COLORS.rose;
    ctx.font = "italic 600 40px 'Inter', sans-serif";
    const evocative = EVOCATIVE[archetype.id] || "Which archetype is drawn to you?";
    wrapText(ctx, `“${evocative}”`, W - 300).forEach(line => {
        ctx.fillText(line, cx, y);
        y += 56;
    });

    // Footer: wordmark + referral URL
    const footY = Math.max(y + 120, H - (format === "feed" ? 190 : 250));
    ctx.fillStyle = COLORS.cream;
    ctx.font = "900 52px 'Inter', sans-serif";
    ctx.fillText("E V E R   A F T E R", cx, footY);
    ctx.fillStyle = COLORS.gold;
    ctx.font = "600 34px 'Inter', sans-serif";
    ctx.fillText(`youreverafter.com/r/${referralCode}`, cx, footY + 62);
    ctx.fillStyle = "rgba(253,249,244,0.55)";
    ctx.font = "400 26px 'Inter', sans-serif";
    ctx.fillText("Take the assessment. Find who fits.", cx, footY + 116);

    return new Promise(resolve => canvas.toBlob(resolve, "image/png"));
}
